import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../../components/Seo';
import { keywordsServices } from '../../seo/keywords';
import { breadcrumbJsonLd } from '../../seo/site';

const ECommerce: React.FC = () => {
  return (
    <div className="pt-24">
      <Seo
        title="E-commerce Website Development | Custom Online Stores"
        description="Custom online stores with catalog, cart, checkout, payments, and order management—built as a standalone e-commerce platform."
        keywords={['e-commerce website development', 'custom online store', 'ecommerce website', ...keywordsServices]}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'E-commerce Website Development', path: '/services/e-commerce' },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-slate-500 dark:text-dark-400">
              <li><Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/services" className="hover:text-slate-900 dark:hover:text-white transition-colors">Services</Link></li>
              <li>/</li>
              <li className="text-slate-900 dark:text-white font-medium">E-commerce Website Development</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-24 h-24 rounded-3xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-8">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">
                E-commerce Website Development
              </h1>
              <p className="text-xl text-primary-600 dark:text-primary-400 font-medium mb-6">
                A complete online store, built for your business
              </p>
              <p className="text-slate-600 dark:text-dark-300 text-lg whitespace-pre-line">
                We design and build a standalone e-commerce platform for your brand—product catalog, cart and checkout, payments, and order management—so you can sell online with a store that is yours from day one.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/contact" className="btn-primary">
                  Get a Quote
                </Link>
                <Link to="/services" className="btn-secondary">
                  View All Services
                </Link>
              </div>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">What you get</h3>
              <ul className="space-y-4">
                {[
                  'Custom storefront tailored to your brand and products',
                  'Product catalog, cart, checkout, and order management',
                  'Secure payment integration for your market',
                  'Responsive design that works on desktop and mobile',
                  'Scope and pricing tailored to your business needs',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-600 dark:text-dark-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              How it <span className="gradient-text">works</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              A clear process from idea to a live online store.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Tell us your goals', description: 'Share your products, brand, and selling needs. We define scope, features, and a timeline that fits your business.' },
              { step: '2', title: 'We build your store', description: 'Our team designs and develops your e-commerce platform—catalog, checkout, payments, and the admin tools you need to run it.' },
              { step: '3', title: 'Go live and sell', description: 'We launch your store, hand over training and documentation, and support you as you start taking orders online.' },
            ].map((item) => (
              <div key={item.step} className="card text-center">
                <div className="text-4xl font-display font-bold text-primary-500/30 mb-4">{item.step}</div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Built as a <span className="gradient-text">standalone</span> platform
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              An independent e-commerce website for your clients and customers—separate from our ERP and payroll products.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: 'Storefront & catalog',
                description: 'Branded product pages, categories, search, and filters so shoppers find what they need quickly.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                ),
              },
              {
                title: 'Checkout & payments',
                description: 'Secure cart and checkout with payment methods suited to your market and customers.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                ),
              },
              {
                title: 'Orders & fulfillment',
                description: 'Order tracking, status updates, and admin tools to manage inventory and fulfillments.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                ),
              },
              {
                title: 'Launch & support',
                description: 'Deployment, training, and ongoing support so your team can run the store confidently.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.title} className="card flex items-center gap-6 p-6">
                <div className="w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white">{item.title}</h3>
                  <p className="text-slate-600 dark:text-dark-400 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured work */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Featured <span className="gradient-text">work</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              A live e-commerce store we built for a client—browse the real site.
            </p>
          </div>

          <a
            href="https://vandalifestyle.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="card group max-w-4xl mx-auto block hover:border-primary-500/30 hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-300 overflow-hidden"
          >
            <div className="grid md:grid-cols-5 gap-0">
              <div className="md:col-span-2 relative min-h-[200px] bg-gradient-to-br from-slate-900 via-slate-800 to-primary-900 flex items-center justify-center p-8">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.25),transparent_55%)]" />
                <div className="relative text-center">
                  <p className="text-xs uppercase tracking-[0.2em] text-primary-300 mb-3">Client project</p>
                  <p className="text-3xl md:text-4xl font-display font-bold text-white">Vanda</p>
                  <p className="text-sm text-slate-300 mt-2">Lifestyle watches</p>
                </div>
              </div>
              <div className="md:col-span-3 p-8 flex flex-col justify-center">
                <span className="inline-flex self-start px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-xs font-medium mb-4">
                  Live store
                </span>
                <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  Vanda Lifestyle
                </h3>
                <p className="text-slate-600 dark:text-dark-400 mb-6">
                  Premium watch e-commerce for men and women—product catalog, secure checkout, and pan-India shipping. Built as a standalone storefront for the Vanda brand.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Product catalog', 'Secure checkout', 'Pan-India shipping'].map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-dark-800 text-slate-600 dark:text-dark-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 font-semibold text-primary-600 dark:text-primary-400 group-hover:gap-4 transition-all duration-300">
                  Visit vandalifestyle.com
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Pricing tailored to your store
              </h2>
              <p className="text-slate-700 dark:text-dark-300 text-lg mb-8 max-w-xl mx-auto">
                Every e-commerce project is different. Tell us about your catalog, features, and timeline—and we'll provide a quote.
              </p>
              <Link to="/contact" className="btn-primary">
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ECommerce;
